import { tutorials, tutorialCategories } from "@/data/tutorials";
// Site configuration
export const siteConfig = {
  // Basic site info
  name: "Gaurav Gadhari",
  title: "Gaurav Gadhari | Visionary Developer & AI Architect",
  description: "Portfolio of Gaurav Gadhari - Innovative Software Engineer, AI Systems Architect, and Next-Gen Technology Pioneer",
  url: "https://gaurav-gadhari.vercel.app", // Replace with actual URL

  // Meta data
  keywords: [
    "Software Engineer",
    "AI Architect",
    "Technology Pioneer",
    "Innovation Leader",
    "Full Stack Developer",
    "Code Artisan",
    "System Designer",
    "React Specialist",
    "FastAPI Expert",
    "Supabase Developer",
    "Groq Integration"
  ],

  // Sound effects configuration
  soundEffects: {
    enabled: true, // Master toggle for all sound effects
    // Individual sound effect toggles
    hover: true,
    click: true,
    transition: true,
    success: true,
    error: true,
    typing: true,
    scroll: false,
    notification: true,
    // Volume configuration (0-1)
    masterVolume: 0.3,
    // Should sounds play on mobile devices
    enableOnMobile: false,
  },

  // Navigation links
  navigation: [
    { name: "Home", href: "/" },
    { name: "About", href: "/#about" },
    { name: "Skills", href: "/#skills" },
    { name: "Projects", href: "/#projects" },
    { name: "Tutorials", href: "/tutorials" },
  ],

  // Hero section
  hero: {
    title: "GAURAV GADHARI",
    subtitle: "VISIONARY DEVELOPER & AI ARCHITECT",
    description: "Architecting tomorrow's digital landscape through innovative solutions, cutting-edge AI, and boundless creativity.",
    ctaText: "DISCOVER MY JOURNEY",
  },

  // About section
  about: {
    title: "WHO I AM",
    description: "My coding journey began in 7th grade, drawing inspiration from brilliant minds like Senku and Gen Asagiri. I specialize in crafting sophisticated applications, launching ventures, and designing AI ecosystems that push technological boundaries. Every line of code I write is a step toward reshaping our digital future.",
    quote: "I don't just develop software—I architect digital experiences that challenge the status quo.",
    showSection: true,
  },

  // Projects section
  projects: {
    title: "MY PORTFOLIO",
    description: "Innovative solutions and breakthrough technologies from concept to reality.",
    showSection: true,
    items: [
      {
        id: "dochouse",
        title: "DocHouse",
        description: "Secure document management app with Google Drive sync, biometric authentication, smart categorization, and seamless document sharing.",
        fullDescription: `DocHouse is your ultimate document management companion, designed to keep all your important documents organized, secure, and accessible anywhere. Whether you need to store ID cards, certificates, bills, or any personal documents, DocHouse makes it effortless with powerful features and cloud sync.

**Key Features:**

- **Google Sign-In:** Secure authentication with your Google account for seamless access.
- **Google Drive Sync:** Automatically backup and sync your documents to Google Drive with your permission.
- **Smart Categorization:** Organize documents with custom categories, tags, and metadata for easy retrieval.
- **Biometric Security:** Protect sensitive documents with fingerprint or face authentication.
- **Document Scanning:** Capture and digitize physical documents directly within the app.
- **Push Notifications:** Stay updated with document expiry reminders and important updates.
- **Document Sharing:** Securely share documents with other DocHouse users with granular permissions.
- **Offline Access:** Access your documents even without internet connection.
- **Profile Management:** Store your personal information for quick autofill on documents.
- **Encrypted Storage:** All data transmissions are encrypted using industry-standard protocols (HTTPS/TLS).`,
        image: "/projects/docHouse.png",
        tags: ["Productivity", "Document Management", "Mobile App"],
        category: ["productivity", "utility"],
        link: "https://dochouse.in",
        website: "https://dochouse.in",
        technologies: ["React Native", "TypeScript", "Expo", "Supabase", "Google Drive API"],
        yearCreated: 2025,
        status: "Live & Active",
        launchDate: "2025-12-06",
        featured: true
      },
      {
        id: "21-days-of-habit",
        title: "21 Days Of Habit",
        description: "Modern, beautifully designed habit tracker with offline-first experience, motivational quotes, reminders, and customizable themes.",
        fullDescription: `21 Days Of Habit is your modern, beautifully designed companion for building and sticking to new habits. Whether you want to drink more water, exercise, meditate, or learn something new, this app helps you stay on track with powerful features and a delightful user experience.\n\n**Key Features:**\n\n- **Modern UI/UX:** Enjoy a clean, intuitive interface with smooth animations and parallax effects.\n- **Offline-First:** All features work without internet. Your data and reminders are always available.\n- **Flexible Habit Tracking:** Track daily, interval, and multi-completion habits (e.g., twice or thrice a day).\n- **Motivational Quotes:** Get inspired every day with curated motivational quotes.\n- **Progress Visualization:** See your progress with beautiful circular and linear progress indicators.\n- **Reminders & Notifications:** Get daily, interval, and streak reminders—fully offline, no Firebase or external servers.\n- **Streaks & Stats:** Visualize your streaks, completions, and best-performing habits.\n- **Customizable Themes:** Choose between light, dark, or system themes, and adjust contrast for accessibility.\n- **Developer Options:** (Visible only in development builds) Test notifications and ads easily.\n- **Ad Experience:** Minimal, user-friendly ads. First 5 habits are free; after that, watch a rewarded ad to add more. Interstitial ads are shown sparingly based on usage, never disrupting your flow.`,
        image: "/projects/21-Days-Of-Habit.png",
        tags: ["Productivity", "Wellness", "Mobile App"],
        category: ["productivity", "wellness"],
        link: "https://play.google.com/store/apps/details?id=com.light_computers.daysofhabbit",
        technologies: ["React Native", "TypeScript", "Expo", "AdMob"],
        yearCreated: 2025,
        status: "Live & Active",
        launchDate: "2025-07-28",
        featured: true
      },
      {
        id: "quicksplit",
        title: "QuickSplit",
        description: "Fast and intuitive bill splitting calculator with tip support, split history, and customizable UI preferences.",
        fullDescription: `QuickSplit is your ultimate companion for splitting bills effortlessly. Whether you're dining out with friends, sharing expenses on a trip, or splitting any group costs, QuickSplit makes it quick and easy with powerful features and a beautiful interface.

**Key Features:**

- **Instant Calculations:** Split any bill amount among any number of people with just a few taps.
- **Tip Support:** Add custom tip percentages to your bill before splitting.
- **Split History:** Keep track of all your past splits for easy reference.
- **Customizable UI:** Personalize your experience with adjustable font sizes, currency symbols, and theme options.
- **Offline-First:** All calculations and history are stored locally—no internet required.
- **Privacy-Focused:** Your data stays on your device. No personal information is collected or transmitted.`,
        image: "/projects/quicksplit.png",
        tags: ["Finance", "Utility", "Mobile App"],
        category: ["utility", "finance"],
        link: "https://play.google.com/store/apps/details?id=com.light_timepass.quicksplit",
        technologies: ["React Native", "TypeScript", "Expo", "MMKV", "AdMob"],
        yearCreated: 2025,
        status: "Under Development & Testing",
        launchDate: "2025-12-07",
        featured: true
      },
      {
        id: "tap-the-dot",
        title: "Tap The Dot",
        description: "Fast-paced reaction game where you tap appearing dots to score points. Test your reflexes and compete for high scores!",
        fullDescription: `Tap The Dot is an addictive casual game that tests your reflexes and reaction time. Dots appear randomly on the screen and you must tap them as quickly as possible to score points. Simple to learn, hard to master!

**Key Features:**

- **Simple Gameplay:** Tap the dots as they appear—easy to understand, challenging to master.
- **High Score Tracking:** Your best scores are saved locally so you can always try to beat your record.
- **No Account Required:** Jump straight into the action with no login or signup needed.
- **Suitable for All Ages:** Fun for everyone, from kids to adults.
- **Offline Play:** Play anytime, anywhere—no internet connection required.
- **Privacy-Focused:** No personal data collected. Your scores stay on your device.`,
        image: "/projects/tap-the-dot.png",
        tags: ["Game", "Casual", "Mobile App"],
        category: ["game", "casual"],
        link: "https://play.google.com/store/apps/details?id=com.light_timepass.tapthedot",
        technologies: ["React Native", "TypeScript", "Expo", "AdMob"],
        yearCreated: 2025,
        status: "Under Development & Testing",
        launchDate: "2025-12-17",
        featured: true
      },
      {
        id: "lightx-digital",
        title: "Light X Digital",
        description: "Digital marketing agency website showcasing services, portfolio, and expertise with modern design and strategic content presentation.",
        fullDescription: "Light X Digital represents the cutting edge of digital marketing agency websites, designed to showcase expertise while converting prospects into clients. This comprehensive platform demonstrates advanced web development skills while serving as a powerful business development tool.\n\nThe site features detailed service presentations, case studies, client testimonials, and a portfolio that highlights successful campaigns and projects. Interactive elements and strategic content placement guide visitors through the agency's capabilities and unique value proposition.\n\nBuilt with lead generation in mind, the platform includes contact forms, consultation booking systems, and strategic calls-to-action throughout the user journey. The modern design aesthetic reflects the agency's commitment to staying at the forefront of digital trends.",
        image: "/projects/light-x-digital.png",
        tags: ["Agency", "Digital Marketing", "Business"],
        category: ["business", "marketing"],
        link: "https://lightxdigital.vercel.app/",
        technologies: ["Next.js", "React", "TypeScript", "Tailwind CSS", "Vercel"],
        yearCreated: 2024,
        status: "Live & Active",
        launchDate: "2024-07-20",
        featured: true
      },
      {
        id: "armor-gaming-headphones",
        title: "Armor Gaming Headphones",
        description: "High-performance gaming headphones landing page with immersive design, product showcases, and conversion-optimized layout.",
        fullDescription: "Armor Gaming Headphones showcases the perfect fusion of gaming aesthetics and professional marketing design. This high-converting landing page is engineered to capture the attention of serious gamers and convert visitors into customers through compelling storytelling and strategic design.\n\nThe platform features immersive 3D product visualizations, detailed technical specifications, and customer testimonials that build trust and drive purchasing decisions. Interactive elements and smooth animations create an engaging experience that reflects the premium quality of the gaming headphones.\n\nThis is a redesigned and improved version of the original armor.shop site, featuring enhanced UI/UX, better performance, and modern web technologies. Optimized for conversion, the page includes strategic call-to-action placement, social proof integration, and mobile-first responsive design. Every element is crafted to guide visitors through the buyer's journey and maximize sales potential.",
        image: "/projects/armor-gaming.png",
        tags: ["Landing Page", "Gaming", "Product"],
        category: ["marketing", "product"],
        link: "https://armor-blond.vercel.app/",
        technologies: ["Next.js", "React", "Three.js", "Framer Motion", "Tailwind CSS"],
        yearCreated: 2024,
        status: "Live & Active",
        launchDate: "2024-09-10",
        featured: true
      },
      {
        id: "premium-jewellery-showcase",
        title: "Premium Jewellery Showcase",
        description: "Luxurious e-commerce platform for premium jewelry with elegant design, advanced filtering, and seamless shopping experience.",
        fullDescription: "Premium Jewellery Showcase represents the pinnacle of luxury e-commerce design, crafted specifically for high-end jewelry retailers. This sophisticated platform combines breathtaking visual aesthetics with powerful functionality to create an unparalleled online shopping experience.\n\nThe platform features stunning product galleries with zoom functionality, advanced filtering by metal type, stone, price range, and collection. Each piece is presented with professional photography and detailed specifications, allowing customers to appreciate the craftsmanship and quality that defines premium jewelry.\n\n**Currently for sale** - This complete, production-ready e-commerce solution is available for purchase and can be customized for your jewelry business needs. Built with performance optimization and conversion in mind, the site delivers lightning-fast loading times, smooth animations, and an intuitive checkout process that instills confidence in luxury purchases. The responsive design ensures the jewelry looks stunning on every device, from desktop to mobile.",
        image: "/projects/premium-jewellery.png",
        tags: ["E-commerce", "Luxury", "Jewelry", "For Sale"],
        category: ["ecommerce", "luxury"],
        link: "https://premium-jewellery-showcase-2ndx.vercel.app/",
        technologies: ["Next.js", "React", "TypeScript", "Tailwind CSS", "Vercel"],
        yearCreated: 2024,
        status: "Live & Active",
        launchDate: "2024-08-15",
        featured: true
      },
      {
        id: "team-lucid",
        title: "Team Lucid",
        description: "Professional team collaboration platform with modern UI, project management features, and streamlined communication tools.",
        fullDescription: "Team Lucid revolutionizes team collaboration by providing a comprehensive platform that combines project management, communication, and productivity tools in one seamless interface. Designed for modern teams who demand efficiency and clarity in their workflows.\n\nThe platform features real-time collaboration tools, project tracking dashboards, team communication channels, and advanced analytics to help teams stay organized and productive. The intuitive interface reduces the learning curve while providing powerful functionality for complex project management needs.\n\nBuilt with scalability and performance in mind, Team Lucid handles teams of all sizes with enterprise-grade security and reliability. The responsive design ensures team members can stay connected and productive from any device, anywhere.",
        image: "/projects/team-lucid.png",
        tags: ["Collaboration", "SaaS", "Team Management"],
        category: ["saas", "productivity"],
        link: "https://team-lucid.vercel.app/",
        technologies: ["Next.js", "React", "TypeScript", "Tailwind CSS", "WebSocket"],
        yearCreated: 2024,
        status: "Live & Active",
        launchDate: "2024-06-05",
        featured: true
      },
      {
        id: "daily-rise-wellness",
        title: "Daily Rise Wellness",
        description: "Comprehensive health and wellness e-commerce platform delivering premium products with seamless user experience and modern design aesthetics.",
        fullDescription: "Daily Rise Wellness represents a new standard in health and wellness e-commerce, combining modern web technologies with intuitive user experience design. This platform goes beyond traditional online stores by creating a holistic wellness ecosystem that educates, inspires, and empowers customers on their health journey.\n\nThe platform features a carefully curated selection of wellness products, from supplements and fitness equipment to mindfulness tools and nutritional guidance. Each product is presented with comprehensive information, customer reviews, and educational content to help users make informed decisions about their health investments.\n\nBuilt with performance and accessibility in mind, the site delivers lightning-fast loading times, responsive design across all devices, and an intuitive checkout process that converts visitors into satisfied customers. The modern design aesthetic creates a calming, trustworthy environment that reflects the brand's commitment to authentic wellness.",
        image: "/projects/dailyrise-wellness.png",
        tags: ["E-commerce", "SaaS"],
        category: ["saas"],
        link: "https://daily-rise-wellness.vercel.app/",
        technologies: ["React", "Next.js", "TypeScript", "Tailwind CSS", "Vercel"],
        yearCreated: 2024,
        status: "Live & Active",
        launchDate: "2024-5-30",
        featured: true
      },
      {
        id: "clipmind",
        title: "ClipMind",
        description: "Revolutionary AI-enhanced clipboard ecosystem that redefines productivity through intelligent content organization, semantic understanding, and automated workflow optimization.",
        fullDescription: "ClipMind represents a paradigm shift in how we interact with our digital clipboard. This isn't just another clipboard manager—it's an intelligent assistant that understands, organizes, and enhances every piece of content you copy.\n\nThe application leverages advanced AI algorithms to automatically categorize content, detect patterns in your workflow, and provide contextual suggestions. Whether you're managing code snippets, research notes, or creative content, ClipMind adapts to your unique working style.\n\nPowered by Groq API and LLaMA 3 70B, ClipMind delivers instantaneous AI processing while maintaining complete user privacy. The React Native architecture ensures seamless performance across all devices, making productivity truly portable.",
        image: "/projects/clipmind.png",
        tags: ["AI", "Productivity"],
        category: ["ai", "productivity"],
        link: "https://play.google.com/store/apps/details?id=com.lightcomputers.clipmind",
        technologies: ["React Native", "Groq API", "LLaMA 3", "AI", "JavaScript"],
        yearCreated: 2025,
        status: "Beta Testing Phase",
        launchDate: "2025-06-01",
        featured: true
      },
    ]
  },

  // Tutorials section
  tutorials: {
    title: "TUTORIALS",
    description: "Learn. Build. Innovate.\n\nKnowledge shared is knowledge multiplied. Here's everything I've learned on my journey from 7th grade to building the future.",
    showSection: true,
    items: tutorials,
    categories: tutorialCategories,
  },

  // Skills section
  skills: {
    title: "EXPERTISE",
    description: "Core competencies and technical mastery across the development spectrum",
    showSection: true,
    categories: [
      {
        name: "User Interface",
        skills: ["React", "Next.js", "TypeScript", "Tailwind CSS", "Three.js"]
      },
      {
        name: "Server Systems",
        skills: ["Node.js", "FastAPI", "Go", "Django", "Supabase"]
      },
      {
        name: "Intelligence & Learning",
        skills: ["Groq API", "TensorFlow", "Deep Learning", "NLP", "LLaMA"]
      },
      {
        name: "Infrastructure & Automation",
        skills: ["Docker", "Kubernetes", "AWS", "GCP", "CI/CD"]
      }
    ]
  },

  // Resume section
  resume: {
    title: "PROFESSIONAL PROFILE",
    description: "Comprehensive technical mastery encompassing the complete development ecosystem. Gaurav excels at designing sophisticated user experiences, engineering scalable backend architectures, and creating maintainable, high-performance solutions that stand the test of time.",
    showSection: true,
    filePath: "/resume/Gaurav_Gadhari_Resume.pdf",
    buttonText: "View Complete Resume"
  },

  // Contact section
  contact: {
    title: "LET'S CONNECT",
    description: "Ready to collaborate on groundbreaking projects or explore new opportunities together.",
    showSection: true,
    email: "gauravgadhari39@gmail.com",
    phone: "+91 77210 42911",
    socialLinks: [
      { name: "GitHub", url: "https://github.com/GauravGadhari" },
      { name: "LinkedIn", url: "https://www.linkedin.com/in/gaurav-gadhari-579558275/" },
      { name: "Twitter", url: "https://x.com/AGauravHere" },
      { name: "Instagram", url: "https://www.instagram.com/a_gaurav_here/" },
      { name: "YouTube", url: "https://www.youtube.com/@codewithgaurav37" },
    ]
  },

  // Footer section
  footer: {
    text: "© 2025 Gaurav Gadhari. Crafted with passion and precision.",
  }
};
